import api from '../api/client';
// pages/ConsumptionPage.js
import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Row,
    Col,
    Space,
    Tag,
    message,
    Divider,
    Breadcrumb,
    Statistic,
    Alert,
    Descriptions,
    Modal,
    Table,
    Tabs,
    Typography,
    AutoComplete,
    Badge,
    Radio,
    Switch
} from 'antd';
import {
    ArrowLeftOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    ShoppingOutlined,
    HomeOutlined,
    CalculatorOutlined,
    SearchOutlined,
    TeamOutlined,
    MedicineBoxOutlined,
    HeartOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const ConsumptionPage = () => {
    // const navigate = useNavigate();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [consumptionItems, setConsumptionItems] = useState([]);
    const [stockItems, setStockItems] = useState([]);
    const [searchProducts, setSearchProducts] = useState([]);
    const [totalCost, setTotalCost] = useState(0);
    const [animalGroups, setAnimalGroups] = useState([]);
    const [selectedAnimalGroup, setSelectedAnimalGroup] = useState(null);

    // Consumption types
    const consumptionTypes = {
        feeding: { label: 'Feeding', color: 'green', icon: <HeartOutlined /> },
        vaccination: { label: 'Vaccination', color: 'red', icon: <MedicineBoxOutlined /> },
        supplementation: { label: 'Supplementation', color: 'blue', icon: <PlusOutlined /> },
        treatment: { label: 'Treatment', color: 'orange', icon: <MedicineBoxOutlined /> },
        other: { label: 'Other', color: 'gray', icon: <FileTextOutlined /> }
    };

    // Animal types
    const animalTypes = [
        { value: 'chicken', label: 'Chicken' },
        { value: 'cattle', label: 'Cattle' },
        { value: 'goat', label: 'Goat' },
        { value: 'sheep', label: 'Sheep' },
        { value: 'pig', label: 'Pig' },
        { value: 'fish', label: 'Fish' },
        { value: 'other', label: 'Other' }
    ];

    // Fetch stock items (only those with available stock)
    const fetchStockItems = async () => {
        try {
            const response = await fetch('/api/stock/current?available=true');
            const result = await response.json();
            if (result.success) {
                setStockItems(result.data.filter((item) => item.current_quantity > 0));
            }
        } catch (error) {
            console.error('Error fetching stock items:', error);
        }
    };

    // Fetch animal groups
    const fetchAnimalGroups = async () => {
        try {
            const response = await fetch('/api/animals/groups');
            const result = await response.json();
            if (result.success) {
                setAnimalGroups(result.data);
            }
        } catch (error) {
            console.error('Error fetching animal groups:', error);
        }
    };

    // Calculate total cost
    useEffect(() => {
        const total = consumptionItems.reduce((sum, item) => sum + (item.total_cost || 0), 0);
        setTotalCost(total);
        form.setFieldsValue({ total_cost: total });
    }, [consumptionItems, form]);

    // Add consumption item
    const handleAddItem = () => {
        const newItem = {
            id: Date.now(),
            stock_item_id: null,
            product_name: '',
            quantity_used: 1,
            unit: 'kg',
            unit_cost: 0,
            total_cost: 0,
            batch_used: ''
        };
        setConsumptionItems([...consumptionItems, newItem]);
    };

    // Update consumption item
    const handleUpdateItem = (id, field, value) => {
        setConsumptionItems(
            consumptionItems.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value };

                    // Auto-calculate total cost
                    if (field === 'quantity_used' || field === 'unit_cost') {
                        updatedItem.total_cost = (updatedItem.quantity_used || 0) * (updatedItem.unit_cost || 0);
                    }

                    return updatedItem;
                }
                return item;
            })
        );
    };

    // Delete consumption item
    const handleDeleteItem = (id) => {
        setConsumptionItems(consumptionItems.filter((item) => item.id !== id));
    };

    // Search stock items
    const handleSearchStockItem = (value) => {
        if (!value) {
            setSearchProducts([]);
            return;
        }

        const filtered = stockItems.filter(
            (item) =>
                item.product_name.toLowerCase().includes(value.toLowerCase()) || item.brand?.toLowerCase().includes(value.toLowerCase())
        );

        setSearchProducts(filtered.slice(0, 10));
    };

    // Select stock item
    const handleSelectStockItem = (value, option, itemIndex) => {
        const product = option.item;
        const updatedItems = [...consumptionItems];
        updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            stock_item_id: product.id,
            product_name: product.product_name,
            product_type: product.product_type,
            brand: product.brand,
            unit: product.unit,
            unit_cost: product.unit_cost,
            batch_used: product.batch_number || '',
            total_cost: updatedItems[itemIndex].quantity_used * product.unit_cost
        };
        setConsumptionItems(updatedItems);
    };

    // Save consumption log
    const handleSaveConsumption = async (values) => {
        if (consumptionItems.length === 0) {
            message.error('Please add at least one item used');
            return;
        }

        // Check if quantities are available
        for (const item of consumptionItems) {
            const stockItem = stockItems.find((si) => si.id === item.stock_item_id);
            if (stockItem && item.quantity_used > stockItem.current_quantity) {
                message.error(`Insufficient stock for ${item.product_name}. Available: ${stockItem.current_quantity} ${stockItem.unit}`);
                return;
            }
        }

        setSaving(true);
        try {
            const consumptionData = {
                ...values,
                consumption_date: values.consumption_date.format('YYYY-MM-DD'),
                total_cost: totalCost,
                items: consumptionItems,
                administered_by: 1, // From auth context
                administered_by_name: 'Admin' // From auth context
            };

            const response = await fetch('/api/stock/consumption', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(consumptionData)
            });

            const result = await response.json();

            if (result.success) {
                message.success('Consumption logged successfully!');
                // Reset form
                form.resetFields();
                setConsumptionItems([]);
                setTotalCost(0);
                fetchStockItems(); // Refresh stock levels
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error logging consumption:', error);
            message.error('Failed to log consumption');
        } finally {
            setSaving(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchStockItems();
        fetchAnimalGroups();
    }, []);

    // Consumption item columns
    const consumptionItemColumns = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (_, __, index) => index + 1
        },
        {
            title: 'Product',
            key: 'product',
            width: 250,
            render: (_, record, index) => (
                <AutoComplete
                    options={searchProducts.map((item) => ({
                        value: item.product_name,
                        label: (
                            <div>
                                <Tag color={record.product_type === 'feed' ? 'green' : 'blue'} size="small">
                                    {record.product_type}
                                </Tag>
                                <strong>{item.product_name}</strong>
                                {item.brand && <small> ({item.brand})</small>}
                                <div>
                                    <small>
                                        Available: {item.current_quantity} {item.unit}
                                    </small>
                                </div>
                            </div>
                        ),
                        item: item
                    }))}
                    value={record.product_name}
                    onSelect={(value, option) => handleSelectStockItem(value, option, index)}
                    onSearch={handleSearchStockItem}
                    placeholder="Search product..."
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Quantity Used',
            dataIndex: 'quantity_used',
            key: 'quantity_used',
            width: 120,
            render: (_, record) => (
                <InputNumber
                    value={record.quantity_used}
                    onChange={(value) => handleUpdateItem(record.id, 'quantity_used', value)}
                    min={0.01}
                    step={0.01}
                    style={{ width: '100%' }}
                    size="small"
                />
            )
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            width: 80,
            render: (unit) => <Text>{unit}</Text>
        },
        {
            title: 'Unit Cost',
            dataIndex: 'unit_cost',
            key: 'unit_cost',
            width: 100,
            render: (cost) => `KES ${cost}`
        },
        {
            title: 'Total Cost',
            dataIndex: 'total_cost',
            key: 'total_cost',
            width: 100,
            render: (cost) => <Text strong>KES {cost}</Text>
        },
        {
            title: 'Batch Used',
            dataIndex: 'batch_used',
            key: 'batch_used',
            width: 120,
            render: (_, record) => (
                <Input
                    value={record.batch_used}
                    onChange={(e) => handleUpdateItem(record.id, 'batch_used', e.target.value)}
                    placeholder="Batch number"
                    size="small"
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteItem(record.id)} size="small" />
            )
        }
    ];

    return (
        <>
            {/* Breadcrumb */}
            {/* <Breadcrumb style={{ marginBottom: '16px' }}>
                <Breadcrumb.Item>
                    <Link to="/">
                        <HomeOutlined /> Home
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/stock">
                        <ShoppingOutlined /> Stock Management
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Consumption Log</Breadcrumb.Item>
            </Breadcrumb> */}

            {/* Page Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div>
                            <Title level={3} style={{ margin: 0 }}>
                                Log Consumption
                            </Title>
                            <Text type="secondary">Record usage of feeds, vaccines, supplements, etc.</Text>
                        </div>
                    </div>
                </Col>
                <Col>
                    <Space>
                        <Button>Cancel</Button>
                        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
                            Save
                        </Button>
                        <Link to="/inventory">
                            <Button icon={<ArrowLeftOutlined />}>Back to Inventory</Button>
                        </Link>
                    </Space>
                </Col>
            </Row>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveConsumption}
                initialValues={{
                    consumption_date: moment(),
                    consumption_type: 'feeding',
                    animal_count: 1
                }}
            >
                <Row gutter={[16, 16]}>
                    {/* Consumption Details */}
                    <Col xs={24} lg={8}>
                        <Card title="Consumption Details">
                            <Form.Item
                                label="Consumption Type"
                                name="consumption_type"
                                rules={[{ required: true, message: 'Please select consumption type' }]}
                            >
                                <Radio.Group>
                                    {Object.entries(consumptionTypes).map(([key, type]) => (
                                        <Radio.Button key={key} value={key}>
                                            {type.icon} {type.label}
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item
                                label="Consumption Date"
                                name="consumption_date"
                                rules={[{ required: true, message: 'Please select date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>

                            <Form.Item label="Animal Type" name="animal_type">
                                <Select placeholder="Select animal type">
                                    {animalTypes.map((type) => (
                                        <Option key={type.value} value={type.value}>
                                            {type.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Animal Group" name="animal_group_id">
                                <Select placeholder="Select animal group" onChange={setSelectedAnimalGroup} showSearch>
                                    {animalGroups.map((group) => (
                                        <Option key={group.id} value={group.id}>
                                            <Space>
                                                <TeamOutlined />
                                                {group.group_name}
                                                <small>({group.animal_count} animals)</small>
                                            </Space>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Number of Animals" name="animal_count">
                                <InputNumber style={{ width: '100%' }} min={1} placeholder="Number of animals" />
                            </Form.Item>

                            <Form.Item label="Administered By" name="administered_by">
                                <Input placeholder="Person who administered" prefix={<UserOutlined />} />
                            </Form.Item>
                        </Card>

                        {/* Summary Card */}
                        <Card title="Summary" style={{ marginTop: '16px' }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Statistic
                                    title="Total Cost"
                                    value={totalCost}
                                    precision={2}
                                    valueStyle={{ color: '#3f8600' }}
                                    prefix="KES"
                                />
                                <Statistic title="Items Count" value={consumptionItems.length} valueStyle={{ color: '#1890ff' }} />
                                {selectedAnimalGroup && (
                                    <Alert
                                        message={`Group: ${selectedAnimalGroup.group_name}`}
                                        description={`${selectedAnimalGroup.animal_count} animals`}
                                        type="info"
                                        showIcon
                                    />
                                )}
                            </Space>
                        </Card>

                        {/* Notes Card */}
                        <Card title="Additional Information" style={{ marginTop: '16px' }}>
                            <Form.Item label="Notes" name="notes">
                                <TextArea rows={4} placeholder="Additional notes about this consumption..." />
                            </Form.Item>
                        </Card>
                    </Col>

                    {/* Consumption Items */}
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Space>
                                            <span>Items Used ({consumptionItems.length})</span>
                                            {consumptionItems.length > 0 && (
                                                <Badge count={consumptionItems.length} style={{ backgroundColor: '#1890ff' }} />
                                            )}
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem}>
                                                Add Item
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            }
                        >
                            {consumptionItems.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <CalculatorOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                    <Title level={4}>No items added</Title>
                                    <Text type="secondary">Add items that were consumed/used for animals</Text>
                                    <br />
                                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem} style={{ marginTop: '16px' }}>
                                        Add Your First Item
                                    </Button>
                                </div>
                            ) : (
                                <Table
                                    dataSource={consumptionItems}
                                    columns={consumptionItemColumns}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 1300 }}
                                    summary={() => (
                                        <Table.Summary fixed="bottom">
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={6} align="right">
                                                    <Text strong>Total Cost:</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1}>
                                                    <Title level={5} style={{ margin: 0 }}>
                                                        KES {totalCost.toFixed(2)}
                                                    </Title>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} />
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    )}
                                />
                            )}

                            {/* Stock Warning */}
                            {consumptionItems.length > 0 && (
                                <Alert
                                    message="Stock Availability Check"
                                    description="Make sure you have sufficient stock for all items before saving."
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: '16px' }}
                                />
                            )}
                        </Card>

                        {/* Quick Templates */}
                        <Card title="Quick Templates" style={{ marginTop: '16px' }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={8}>
                                    <Button
                                        block
                                        icon={<HeartOutlined />}
                                        onClick={() => {
                                            form.setFieldsValue({ consumption_type: 'feeding' });
                                            message.info('Feeding template loaded');
                                        }}
                                    >
                                        Feeding Template
                                    </Button>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Button
                                        block
                                        icon={<MedicineBoxOutlined />}
                                        onClick={() => {
                                            form.setFieldsValue({ consumption_type: 'vaccination' });
                                            message.info('Vaccination template loaded');
                                        }}
                                    >
                                        Vaccination Template
                                    </Button>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Button block type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
                                        Save & Continue
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default ConsumptionPage;
